import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';
const STORAGE_KEY = 'vusub_admin_token';
const STORAGE_USER_KEY = 'vusub_admin_user';

// ─── Styled Components ─────────────────────────────────────────────────────────

const Portal = styled.div`
  min-height: 100vh;
  background: #0a0a0f;
  color: #e0e0e0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const TopBar = styled.div`
  background: #111118;
  border-bottom: 1px solid #222;
  padding: 14px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled.span`
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #ff4757;
  text-transform: uppercase;
`;

const AdminBadge = styled.span`
  font-size: 0.75rem;
  background: #1a1a2e;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 3px 8px;
  color: #888;
`;

const SignOutBtn = styled.button`
  background: none;
  border: 1px solid #333;
  color: #888;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  &:hover { border-color: #ff4757; color: #ff4757; }
`;

// ─── Login Form ────────────────────────────────────────────────────────────────

const LoginWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #0a0a0f;
`;

const LoginCard = styled.div`
  background: #111118;
  border: 1px solid #222;
  border-radius: 12px;
  padding: 40px 36px;
  width: 340px;
`;

const LoginTitle = styled.h1`
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0 0 8px;
  color: #fff;
`;

const LoginSub = styled.p`
  font-size: 0.82rem;
  color: #666;
  margin: 0 0 28px;
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.78rem;
  color: #888;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Input = styled.input`
  width: 100%;
  background: #1a1a24;
  border: 1px solid ${props => props.$error ? '#ff4757' : '#2a2a36'};
  border-radius: 7px;
  color: #e0e0e0;
  font-size: 0.95rem;
  padding: 10px 12px;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #ff4757; }
`;

const LoginBtn = styled.button`
  width: 100%;
  background: #ff4757;
  color: #fff;
  border: none;
  border-radius: 7px;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 12px;
  cursor: pointer;
  margin-top: 8px;
  &:hover { background: #e0404f; }
  &:disabled { background: #444; cursor: default; }
`;

const ErrorMsg = styled.p`
  color: #ff4757;
  font-size: 0.82rem;
  margin: 10px 0 0;
  text-align: center;
`;

// ─── Dashboard ─────────────────────────────────────────────────────────────────

const DashContent = styled.div`
  padding: 32px 28px;
  max-width: 1200px;
  margin: 0 auto;
`;

const TabBar = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 28px;
  border-bottom: 1px solid #1e1e28;
`;

const TabBtn = styled.button`
  background: none;
  border: none;
  border-bottom: 2px solid ${props => props.$active ? '#ff4757' : 'transparent'};
  color: ${props => props.$active ? '#fff' : '#666'};
  font-size: 0.88rem;
  font-weight: ${props => props.$active ? '600' : '400'};
  padding: 8px 20px 10px;
  cursor: pointer;
  margin-bottom: -1px;
  transition: color 0.15s;
  &:hover { color: ${props => props.$active ? '#fff' : '#bbb'}; }
`;

const SearchInput = styled.input`
  background: #1a1a24;
  border: 1px solid #2a2a36;
  border-radius: 7px;
  color: #e0e0e0;
  font-size: 0.88rem;
  padding: 8px 14px;
  outline: none;
  width: 280px;
  &:focus { border-color: #ff4757; }
  &::placeholder { color: #555; }
`;

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const Select = styled.select`
  background: #1a1a24;
  border: 1px solid #2a2a36;
  border-radius: 7px;
  color: #e0e0e0;
  font-size: 0.88rem;
  padding: 8px 12px;
  outline: none;
  cursor: pointer;
  &:focus { border-color: #ff4757; }
`;

const RefreshBtn = styled.button`
  background: #1a1a24;
  border: 1px solid #333;
  color: #ccc;
  padding: 8px 16px;
  border-radius: 7px;
  font-size: 0.85rem;
  cursor: pointer;
  &:hover { border-color: #ff4757; color: #ff4757; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.83rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 14px;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.75rem;
  border-bottom: 1px solid #1e1e28;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px 14px;
  color: #ccc;
  border-bottom: 1px solid #14141e;
  vertical-align: top;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OutcomePill = styled.span`
  display: inline-block;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.$outcome === 'success' ? '#0d2b1a' : '#2b0d0d'};
  color: ${props => props.$outcome === 'success' ? '#2ecc71' : '#ff4757'};
  border: 1px solid ${props => props.$outcome === 'success' ? '#1a5c34' : '#5c1a1a'};
`;

const EmptyMsg = styled.div`
  text-align: center;
  color: #555;
  padding: 48px;
  font-size: 0.9rem;
`;

const LoadingMsg = styled.div`
  text-align: center;
  color: #666;
  padding: 48px;
`;

// ─── Analytics-specific styled components ─────────────────────────────────────

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 14px;
  margin-bottom: 28px;
`;

const StatCard = styled.div`
  background: #111118;
  border: 1px solid #1e1e28;
  border-radius: 10px;
  padding: 18px 20px;
`;

const StatNum = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: #ff4757;
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #666;
  margin-top: 6px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SectionTitle = styled.h3`
  font-size: 0.8rem;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 14px;
`;

const SectionBlock = styled.div`
  margin-bottom: 32px;
`;

// ─── Helpers ───────────────────────────────────────────────────────────────────

function getStoredToken() {
  try { return sessionStorage.getItem(STORAGE_KEY); } catch { return null; }
}
function getStoredUser() {
  try { return sessionStorage.getItem(STORAGE_USER_KEY); } catch { return null; }
}
function saveToken(token, username) {
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
    sessionStorage.setItem(STORAGE_USER_KEY, username);
  } catch { /* ignore */ }
}
function clearToken() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_USER_KEY);
  } catch { /* ignore */ }
}

function isTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch { return true; }
}

function formatTs(isoStr) {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr + (isoStr.endsWith('Z') ? '' : 'Z'));
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return isoStr; }
}

function getMonthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
    opts.push({ val, label });
  }
  return opts;
}

// ─── Main Component ─────────────────────────────────────────────────────────────

export default function AdminPortal() {
  const [token, setToken] = useState(() => {
    const t = getStoredToken();
    return t && !isTokenExpired(t) ? t : null;
  });
  const [adminUser, setAdminUser] = useState(() => getStoredUser() || '');
  const [activeTab, setActiveTab] = useState('logins');

  // Login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Login history tab
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const monthOptions = getMonthOptions();
  const [monthFilter, setMonthFilter] = useState(monthOptions[0].val);
  const [outcomeFilter, setOutcomeFilter] = useState('all');

  // Users tab
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [userSearch, setUserSearch] = useState('');

  // Analytics tab
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');
  const [analyticsMonth, setAnalyticsMonth] = useState(monthOptions[0].val);

  // Agency Requests tab
  const [agencyRequests, setAgencyRequests] = useState([]);
  const [agencyRequestsLoading, setAgencyRequestsLoading] = useState(false);
  const [agencyRequestsError, setAgencyRequestsError] = useState('');
  const [agencyRequestsFilter, setAgencyRequestsFilter] = useState('pending');
  const [rejectModal, setRejectModal] = useState({ open: false, oid: '', name: '', reason: '' });
  const [agencyActionLoading, setAgencyActionLoading] = useState(null); // oid being actioned

  // Catalog tab
  const [catalogVideos, setCatalogVideos] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogEditModal, setCatalogEditModal] = useState(null);
  const [catalogAddModal, setCatalogAddModal] = useState(false);
  const [catalogAddForm, setCatalogAddForm] = useState({ filename: '', title: '', description: '', points: 5, url: '' });
  const [catalogSaving, setCatalogSaving] = useState(false);

  // Ad Review tab
  const [pendingAds, setPendingAds] = useState([]);
  const [pendingAdsLoading, setPendingAdsLoading] = useState(false);
  const [pendingAdsError, setPendingAdsError] = useState('');
  const [adActionLoading, setAdActionLoading] = useState(null); // video_id
  const [adRejectModal, setAdRejectModal] = useState({ open: false, video_id: '', agency_oid: '', title: '', reason: '' });
  const [adActionToast, setAdActionToast] = useState('');

  // Migrations tab
  const [migrationsList, setMigrationsList] = useState([]);
  const [migrationsLoading, setMigrationsLoading] = useState(false);
  const [migrationsError, setMigrationsError] = useState('');
  const [migrationRunning, setMigrationRunning] = useState(null); // id being run
  const [migrationResult, setMigrationResult] = useState('');     // success toast

  // Agencies tab
  const [agencies, setAgencies] = useState([]);
  const [agenciesLoading, setAgenciesLoading] = useState(false);
  const [agenciesError, setAgenciesError] = useState('');
  const [agencyExpandedId, setAgencyExpandedId] = useState(null);
  const [agencyUsers, setAgencyUsers] = useState({}); // { agency_id: [...] }
  const [agencyUsersLoading, setAgencyUsersLoading] = useState(null);
  const [newAgencyName, setNewAgencyName] = useState('');
  const [newAgencyCreating, setNewAgencyCreating] = useState(false);
  const [addUserEmail, setAddUserEmail] = useState('');
  const [addUserRole, setAddUserRole] = useState('member');
  const [addUserLoading, setAddUserLoading] = useState(null); // agency_id being added to
  const [inviteLoading, setInviteLoading] = useState(null); // email
  const [inviteCopied, setInviteCopied] = useState(null); // email
  const [editRoleLoading, setEditRoleLoading] = useState(null); // email being updated
  const [agencyToasts, setAgencyToasts] = useState([]);

  // Publishers tab
  const [publishers, setPublishers] = useState([]);
  const [publishersLoading, setPublishersLoading] = useState(false);
  const [publishersError, setPublishersError] = useState('');
  const [publisherExpandedId, setPublisherExpandedId] = useState(null);
  const [publisherUsers, setPublisherUsers] = useState({});
  const [publisherUsersLoading, setPublisherUsersLoading] = useState(null);
  const [newPublisherName, setNewPublisherName] = useState('');
  const [newPublisherCreating, setNewPublisherCreating] = useState(false);
  const [pubAddUserEmail, setPubAddUserEmail] = useState('');
  const [pubAddUserRole, setPubAddUserRole] = useState('owner');
  const [pubAddUserLoading, setPubAddUserLoading] = useState(null);
  const [pubInviteLoading, setPubInviteLoading] = useState(null);
  const [pubInviteCopied, setPubInviteCopied] = useState(null);
  const [pubEditRoleLoading, setPubEditRoleLoading] = useState(null);
  const [publisherToasts, setPublisherToasts] = useState([]);

  const showAgencyToast = (msg, type = 'ok') => {
    const id = Date.now();
    setAgencyToasts(t => [...t, { id, msg, type }]);
    setTimeout(() => setAgencyToasts(t => t.filter(x => x.id !== id)), 3500);
  };

  const showPublisherToast = (msg, type = 'ok') => {
    const id = Date.now();
    setPublisherToasts(t => [...t, { id, msg, type }]);
    setTimeout(() => setPublisherToasts(t => t.filter(x => x.id !== id)), 3500);
  };

  const fetchPublishers = useCallback(async (tok) => {
    setPublishersLoading(true);
    setPublishersError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/publishers`, { headers: { 'X-Admin-Token': tok } });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setPublishers(data.publishers || []);
    } catch (e) {
      setPublishersError(e.message);
    } finally {
      setPublishersLoading(false);
    }
  }, []);

  const fetchPublisherUsers = useCallback(async (tok, publisherId) => {
    setPublisherUsersLoading(publisherId);
    try {
      const res = await fetch(`${API_URL}/api/admin/publishers/${publisherId}/users`, { headers: { 'X-Admin-Token': tok } });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setPublisherUsers(prev => ({ ...prev, [publisherId]: data.users || [] }));
    } catch (e) {
      showPublisherToast(e.message, 'err');
    } finally {
      setPublisherUsersLoading(null);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreatePublisher = async () => {
    if (!newPublisherName.trim()) return;
    setNewPublisherCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/publishers`, {
        method: 'POST',
        headers: { 'X-Admin-Token': token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newPublisherName.trim() }),
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setNewPublisherName('');
      showPublisherToast('Publisher created');
      fetchPublishers(token);
    } catch (e) {
      showPublisherToast(e.message, 'err');
    } finally {
      setNewPublisherCreating(false);
    }
  };

  const handleTogglePublisherStatus = async (publisher) => {
    const newStatus = publisher.status === 'active' ? 'suspended' : 'active';
    try {
      const res = await fetch(`${API_URL}/api/admin/publishers/${publisher.publisher_id}`, {
        method: 'PATCH',
        headers: { 'X-Admin-Token': token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      showPublisherToast(`Publisher ${newStatus}`);
      fetchPublishers(token);
    } catch (e) {
      showPublisherToast(e.message, 'err');
    }
  };

  const handleAddPublisherUser = async (publisherId) => {
    if (!pubAddUserEmail.trim() || !pubAddUserEmail.includes('@')) { showPublisherToast('Valid email required', 'err'); return; }
    setPubAddUserLoading(publisherId);
    try {
      const res = await fetch(`${API_URL}/api/admin/publishers/${publisherId}/users`, {
        method: 'POST',
        headers: { 'X-Admin-Token': token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: pubAddUserEmail.trim(), role: pubAddUserRole }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || `Error ${res.status}`); }
      setPubAddUserEmail('');
      showPublisherToast('User added');
      fetchPublisherUsers(token, publisherId);
    } catch (e) {
      showPublisherToast(e.message, 'err');
    } finally {
      setPubAddUserLoading(null);
    }
  };

  const handleRemovePublisherUser = async (publisherId, email) => {
    if (!window.confirm(`Remove ${email} from this publisher?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/publishers/${publisherId}/users/${encodeURIComponent(email)}`, {
        method: 'DELETE', headers: { 'X-Admin-Token': token },
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      showPublisherToast('User removed');
      fetchPublisherUsers(token, publisherId);
    } catch (e) {
      showPublisherToast(e.message, 'err');
    }
  };

  const handleEditPublisherUserRole = async (publisherId, email, newRole) => {
    setPubEditRoleLoading(email);
    try {
      const res = await fetch(`${API_URL}/api/admin/publishers/${publisherId}/users/${encodeURIComponent(email)}`, {
        method: 'PATCH',
        headers: { 'X-Admin-Token': token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || `Error ${res.status}`); }
      showPublisherToast('Role updated');
      fetchPublisherUsers(token, publisherId);
    } catch (e) {
      showPublisherToast(e.message, 'err');
    } finally {
      setPubEditRoleLoading(null);
    }
  };

  const handleGeneratePublisherInvite = async (publisherId, email) => {
    setPubInviteLoading(email);
    try {
      const res = await fetch(`${API_URL}/api/admin/publishers/${publisherId}/users/${encodeURIComponent(email)}/invite`, {
        method: 'POST', headers: { 'X-Admin-Token': token },
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || `Error ${res.status}`); }
      const data = await res.json();
      await navigator.clipboard.writeText(data.invite_url);
      setPubInviteCopied(email);
      setTimeout(() => setPubInviteCopied(null), 3000);
      showPublisherToast('Invite link copied to clipboard!');
    } catch (e) {
      showPublisherToast(e.message, 'err');
    } finally {
      setPubInviteLoading(null);
    }
  };

  const fetchAgencies = useCallback(async (tok) => {
    setAgenciesLoading(true);
    setAgenciesError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/agencies`, { headers: { 'X-Admin-Token': tok } });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setAgencies(data.agencies || []);
    } catch (e) {
      setAgenciesError(e.message);
    } finally {
      setAgenciesLoading(false);
    }
  }, []);

  const fetchAgencyUsers = useCallback(async (tok, agencyId) => {
    setAgencyUsersLoading(agencyId);
    try {
      const res = await fetch(`${API_URL}/api/admin/agencies/${agencyId}/users`, { headers: { 'X-Admin-Token': tok } });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setAgencyUsers(prev => ({ ...prev, [agencyId]: data.users || [] }));
    } catch (e) {
      showAgencyToast(e.message, 'err');
    } finally {
      setAgencyUsersLoading(null);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateAgency = async () => {
    if (!newAgencyName.trim()) return;
    setNewAgencyCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/agencies`, {
        method: 'POST',
        headers: { 'X-Admin-Token': token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newAgencyName.trim() }),
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setNewAgencyName('');
      showAgencyToast('Agency created');
      fetchAgencies(token);
    } catch (e) {
      showAgencyToast(e.message, 'err');
    } finally {
      setNewAgencyCreating(false);
    }
  };

  const handleToggleAgencyStatus = async (agency) => {
    const newStatus = agency.status === 'active' ? 'suspended' : 'active';
    try {
      const res = await fetch(`${API_URL}/api/admin/agencies/${agency.agency_id}`, {
        method: 'PATCH',
        headers: { 'X-Admin-Token': token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      showAgencyToast(`Agency ${newStatus}`);
      fetchAgencies(token);
    } catch (e) {
      showAgencyToast(e.message, 'err');
    }
  };

  const handleAddUser = async (agencyId) => {
    if (!addUserEmail.trim() || !addUserEmail.includes('@')) { showAgencyToast('Valid email required', 'err'); return; }
    setAddUserLoading(agencyId);
    try {
      const res = await fetch(`${API_URL}/api/admin/agencies/${agencyId}/users`, {
        method: 'POST',
        headers: { 'X-Admin-Token': token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: addUserEmail.trim(), role: addUserRole }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || `Error ${res.status}`); }
      setAddUserEmail('');
      showAgencyToast('User added');
      fetchAgencyUsers(token, agencyId);
    } catch (e) {
      showAgencyToast(e.message, 'err');
    } finally {
      setAddUserLoading(null);
    }
  };

  const handleRemoveUser = async (agencyId, email) => {
    if (!window.confirm(`Remove ${email} from this agency?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/agencies/${agencyId}/users/${encodeURIComponent(email)}`, {
        method: 'DELETE', headers: { 'X-Admin-Token': token },
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      showAgencyToast('User removed');
      fetchAgencyUsers(token, agencyId);
    } catch (e) {
      showAgencyToast(e.message, 'err');
    }
  };

  const handleEditUserRole = async (agencyId, email, newRole) => {
    setEditRoleLoading(email);
    try {
      const res = await fetch(`${API_URL}/api/admin/agencies/${agencyId}/users/${encodeURIComponent(email)}`, {
        method: 'PATCH',
        headers: { 'X-Admin-Token': token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || `Error ${res.status}`); }
      showAgencyToast('Role updated');
      fetchAgencyUsers(token, agencyId);
    } catch (e) {
      showAgencyToast(e.message, 'err');
    } finally {
      setEditRoleLoading(null);
    }
  };

  const handleGenerateInvite = async (agencyId, email) => {
    setInviteLoading(email);
    try {
      const res = await fetch(`${API_URL}/api/admin/agencies/${agencyId}/users/${encodeURIComponent(email)}/invite`, {
        method: 'POST', headers: { 'X-Admin-Token': token },
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || `Error ${res.status}`); }
      const data = await res.json();
      await navigator.clipboard.writeText(data.invite_url);
      setInviteCopied(email);
      setTimeout(() => setInviteCopied(null), 3000);
      showAgencyToast('Invite link copied to clipboard!');
    } catch (e) {
      showAgencyToast(e.message, 'err');
    } finally {
      setInviteLoading(null);
    }
  };

  const fetchEvents = useCallback(async (tok, month) => {
    setEventsLoading(true);
    setEventsError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/login-events?month=${month}&limit=500`, {
        headers: { 'X-Admin-Token': tok },
      });
      if (res.status === 401) {
        clearToken();
        setToken(null);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEvents(data.events || []);
    } catch (e) {
      setEventsError('Failed to load events: ' + e.message);
    } finally {
      setEventsLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async (tok) => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/users?limit=999`, {
        headers: { 'X-Admin-Token': tok },
      });
      if (res.status === 401) { clearToken(); setToken(null); return; }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setUsers(data.users || []);
    } catch (e) {
      setUsersError('Failed to load users: ' + e.message);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const fetchAgencyRequests = useCallback(async (tok, status) => {
    setAgencyRequestsLoading(true);
    setAgencyRequestsError('');
    try {
      const url = status && status !== 'all'
        ? `${API_URL}/api/admin/agency-requests?status=${status}&limit=500`
        : `${API_URL}/api/admin/agency-requests?limit=500`;
      const res = await fetch(url, { headers: { 'X-Admin-Token': tok } });
      if (res.status === 401) { clearToken(); setToken(null); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAgencyRequests(data.requests || []);
    } catch (e) {
      setAgencyRequestsError('Failed to load agency requests: ' + e.message);
    } finally {
      setAgencyRequestsLoading(false);
    }
  }, []);

  const handleAgencyApprove = async (oid) => {
    setAgencyActionLoading(oid);
    try {
      const res = await fetch(`${API_URL}/api/admin/agency-requests/${oid}/approve`, {
        method: 'PATCH',
        headers: { 'X-Admin-Token': token },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      setAgencyRequests(prev => prev.map(r => r.oid === oid ? { ...r, status: 'approved' } : r));
    } catch (e) {
      setAgencyRequestsError('Approve failed: ' + e.message);
    } finally {
      setAgencyActionLoading(null);
    }
  };

  const handleAgencyReject = async (oid, reason) => {
    setAgencyActionLoading(oid);
    try {
      const res = await fetch(`${API_URL}/api/admin/agency-requests/${oid}/reject`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Token': token },
        body: JSON.stringify({ rejection_reason: reason }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setAgencyRequests(prev => prev.map(r => r.oid === oid ? { ...r, status: 'rejected', rejection_reason: reason } : r));
    } catch (e) {
      setAgencyRequestsError('Reject failed: ' + e.message);
    } finally {
      setAgencyActionLoading(null);
    }
  };

  const fetchCatalog = useCallback(async (tok) => {
    setCatalogLoading(true);
    setCatalogError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/catalog`, {
        headers: { 'X-Admin-Token': tok },
      });
      if (res.status === 401) { clearToken(); setToken(null); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCatalogVideos(data.videos || []);
    } catch (e) {
      setCatalogError('Failed to load catalog: ' + e.message);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  const handleCatalogToggleActive = async (filename, currentlyActive) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/catalog/${encodeURIComponent(filename)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Token': token },
        body: JSON.stringify({ active: !currentlyActive }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setCatalogVideos(prev => prev.map(v => v.filename === filename ? { ...v, active: !currentlyActive } : v));
    } catch (e) {
      setCatalogError('Update failed: ' + e.message);
    }
  };

  const handleCatalogSave = async () => {
    if (!catalogEditModal) return;
    setCatalogSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/catalog/${encodeURIComponent(catalogEditModal.filename)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Token': token },
        body: JSON.stringify({
          title: catalogEditModal.title,
          description: catalogEditModal.description,
          points: Number(catalogEditModal.points),
          url: catalogEditModal.url,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setCatalogVideos(prev => prev.map(v =>
        v.filename === catalogEditModal.filename
          ? { ...v, title: catalogEditModal.title, description: catalogEditModal.description, points: Number(catalogEditModal.points), url: catalogEditModal.url }
          : v
      ));
      setCatalogEditModal(null);
    } catch (e) {
      setCatalogError('Save failed: ' + e.message);
    } finally {
      setCatalogSaving(false);
    }
  };

  const handleCatalogAdd = async () => {
    if (!catalogAddForm.filename.trim() || !catalogAddForm.title.trim()) return;
    setCatalogSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/catalog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Token': token },
        body: JSON.stringify({
          filename: catalogAddForm.filename.trim(),
          title: catalogAddForm.title.trim(),
          description: catalogAddForm.description.trim(),
          points: Number(catalogAddForm.points) || 5,
          url: catalogAddForm.url.trim(),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      setCatalogAddModal(false);
      setCatalogAddForm({ filename: '', title: '', description: '', points: 5, url: '' });
      fetchCatalog(token);
    } catch (e) {
      setCatalogError('Add failed: ' + e.message);
    } finally {
      setCatalogSaving(false);
    }
  };

  const fetchAnalytics = useCallback(async (tok, month) => {
    setAnalyticsLoading(true);
    setAnalyticsError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/view-metrics?month=${month}&limit=5000`, {
        headers: { 'X-Admin-Token': tok },
      });
      if (res.status === 401) { clearToken(); setToken(null); return; }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setAnalyticsData(data);
    } catch (e) {
      setAnalyticsError('Failed to load analytics: ' + e.message);
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  const fetchMigrations = useCallback(async (tok) => {
    setMigrationsLoading(true);
    setMigrationsError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/migrations`, { headers: { 'X-Admin-Token': tok } });
      if (res.status === 401) { clearToken(); setToken(null); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMigrationsList(data.migrations || []);
    } catch (e) {
      setMigrationsError('Failed to load migrations: ' + e.message);
    } finally {
      setMigrationsLoading(false);
    }
  }, []);

  const handleRunMigration = async (mig) => {
    if (mig.orphaned) return;
    const force = mig.status === 'completed';
    const prompt = force
      ? `"${mig.id}" already ran. Re-run it (force)?\n\nMigrations are idempotent, so this is safe — but only do it if you mean to.`
      : `Run migration "${mig.id}"?\n\n${mig.description}`;
    if (!window.confirm(prompt)) return;
    setMigrationRunning(mig.id);
    setMigrationResult('');
    setMigrationsError('');
    try {
      const res = await fetch(
        `${API_URL}/api/admin/migrations/${encodeURIComponent(mig.id)}/run${force ? '?force=true' : ''}`,
        { method: 'POST', headers: { 'X-Admin-Token': token } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      const summary = data.notes && typeof data.notes === 'object' ? JSON.stringify(data.notes) : '';
      setMigrationResult(`${mig.id}: ${data.outcome}${summary ? ' — ' + summary : ''}`);
      setTimeout(() => setMigrationResult(''), 10000);
      fetchMigrations(token);
    } catch (e) {
      setMigrationsError('Run failed: ' + e.message);
    } finally {
      setMigrationRunning(null);
    }
  };

  useEffect(() => {
    if (token && activeTab === 'migrations') fetchMigrations(token);
  }, [token, activeTab, fetchMigrations]);

  useEffect(() => {
    if (token && activeTab === 'logins') fetchEvents(token, monthFilter);
  }, [token, activeTab, monthFilter, fetchEvents]);

  useEffect(() => {
    if (token && activeTab === 'users' && users.length === 0 && !usersLoading) {
      fetchUsers(token);
    }
  }, [token, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (token && activeTab === 'analytics') fetchAnalytics(token, analyticsMonth);
  }, [token, activeTab, analyticsMonth, fetchAnalytics]);

  useEffect(() => {
    if (token && activeTab === 'agency') fetchAgencyRequests(token, agencyRequestsFilter);
  }, [token, activeTab, agencyRequestsFilter, fetchAgencyRequests]);

  useEffect(() => {
    if (token && activeTab === 'agencies' && agencies.length === 0 && !agenciesLoading) {
      fetchAgencies(token);
    }
  }, [token, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (token && activeTab === 'publishers' && publishers.length === 0 && !publishersLoading) {
      fetchPublishers(token);
    }
  }, [token, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (token && activeTab === 'catalog' && catalogVideos.length === 0 && !catalogLoading) {
      fetchCatalog(token);
    }
  }, [token, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (token && activeTab === 'adreview') {
      setPendingAdsLoading(true);
      setPendingAdsError('');
      fetch(`${API_URL}/api/admin/agency-videos/pending`, { headers: { 'X-Admin-Token': token } })
        .then(r => r.ok ? r.json() : r.json().then(d => { throw new Error(d.detail || `Error ${r.status}`); }))
        .then(d => setPendingAds(d.videos || []))
        .catch(e => setPendingAdsError(e.message))
        .finally(() => setPendingAdsLoading(false));
    }
  }, [token, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      if (res.status === 401) {
        setLoginError('Invalid username or password.');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      saveToken(data.token, data.username);
      setToken(data.token);
      setAdminUser(data.username);
    } catch (e) {
      setLoginError('Login failed: ' + e.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSignOut = () => {
    clearToken();
    setToken(null);
    setAdminUser('');
    setEvents([]);
    setUsers([]);
  };

  const filteredEvents = events.filter(ev =>
    outcomeFilter === 'all' || ev.outcome === outcomeFilter
  );

  const filteredUsers = users.filter(u => {
    if (!userSearch.trim()) return true;
    const q = userSearch.toLowerCase();
    return (
      (u.email || '').toLowerCase().includes(q) ||
      (u.displayName || '').toLowerCase().includes(q) ||
      (u.givenName || '').toLowerCase().includes(q) ||
      (u.surname || '').toLowerCase().includes(q)
    );
  });

  const filteredCatalog = catalogVideos.filter(v => {
    if (!catalogSearch.trim()) return true;
    const q = catalogSearch.toLowerCase();
    return (
      (v.filename || '').toLowerCase().includes(q) ||
      (v.title || '').toLowerCase().includes(q)
    );
  });

  // ─── Login Screen ────────────────────────────────────────────────────────────
  if (!token) {
    return (
      <LoginWrapper>
        <LoginCard>
          <LoginTitle>VuSub Internal</LoginTitle>
          <LoginSub>Admin access only. Credentials issued by engineering.</LoginSub>
          <form onSubmit={handleLogin}>
            <Field>
              <Label htmlFor="ap-user">Username</Label>
              <Input
                id="ap-user"
                type="text"
                autoComplete="username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                $error={!!loginError}
                required
              />
            </Field>
            <Field>
              <Label htmlFor="ap-pass">Password</Label>
              <Input
                id="ap-pass"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                $error={!!loginError}
                required
              />
            </Field>
            <LoginBtn type="submit" disabled={loginLoading}>
              {loginLoading ? 'Signing in…' : 'Sign In'}
            </LoginBtn>
            {loginError && <ErrorMsg>{loginError}</ErrorMsg>}
          </form>
        </LoginCard>
      </LoginWrapper>
    );
  }

  // ─── Dashboard ───────────────────────────────────────────────────────────────
  return (
    <Portal>
      <TopBar>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Logo>VuSub</Logo>
          <AdminBadge>Internal Admin</AdminBadge>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: '0.82rem', color: '#666' }}>{adminUser}</span>
          <SignOutBtn onClick={handleSignOut}>Sign Out</SignOutBtn>
        </div>
      </TopBar>

      <DashContent>
        <TabBar>
          <TabBtn $active={activeTab === 'logins'} onClick={() => setActiveTab('logins')}>
            Login History
          </TabBtn>
          <TabBtn $active={activeTab === 'users'} onClick={() => setActiveTab('users')}>
            Users
          </TabBtn>
          <TabBtn $active={activeTab === 'analytics'} onClick={() => setActiveTab('analytics')}>
            Analytics
          </TabBtn>
          <TabBtn $active={activeTab === 'agencies'} onClick={() => setActiveTab('agencies')}>
            Agencies
          </TabBtn>
          <TabBtn $active={activeTab === 'publishers'} onClick={() => setActiveTab('publishers')}>
            Publishers
          </TabBtn>
          <TabBtn $active={activeTab === 'catalog'} onClick={() => setActiveTab('catalog')}>
            Catalog
          </TabBtn>
          <TabBtn $active={activeTab === 'adreview'} onClick={() => setActiveTab('adreview')}>
            Ad Review {pendingAds.length > 0 && activeTab !== 'adreview' ? `(${pendingAds.length})` : ''}
          </TabBtn>
          <TabBtn $active={activeTab === 'migrations'} onClick={() => setActiveTab('migrations')}>
            Migrations
          </TabBtn>
        </TabBar>

        {/* ── Login History Tab ───────────────────────────────────────────── */}
        {activeTab === 'logins' && (
          <>
            <ControlRow>
              <Select value={monthFilter} onChange={e => setMonthFilter(e.target.value)}>
                {monthOptions.map(({ val, label }) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </Select>
              <Select value={outcomeFilter} onChange={e => setOutcomeFilter(e.target.value)}>
                <option value="all">All outcomes</option>
                <option value="success">Success only</option>
                <option value="failure">Failures only</option>
              </Select>
              <RefreshBtn onClick={() => fetchEvents(token, monthFilter)} disabled={eventsLoading}>
                {eventsLoading ? 'Loading…' : 'Refresh'}
              </RefreshBtn>
              <span style={{ fontSize: '0.8rem', color: '#555', marginLeft: 'auto' }}>
                {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
              </span>
            </ControlRow>

            {eventsError && <ErrorMsg style={{ textAlign: 'left', marginBottom: 20 }}>{eventsError}</ErrorMsg>}

            {eventsLoading ? (
              <LoadingMsg>Loading events…</LoadingMsg>
            ) : filteredEvents.length === 0 ? (
              <EmptyMsg>No login events for this period.</EmptyMsg>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Time</Th>
                    <Th>Email</Th>
                    <Th>Outcome</Th>
                    <Th>IP Address</Th>
                    <Th>User Agent</Th>
                    <Th>Error Code</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEvents.map(ev => (
                    <tr key={ev.RowKey || ev.rowKey}>
                      <Td title={ev.timestamp}>{formatTs(ev.timestamp)}</Td>
                      <Td title={ev.email}>{ev.email || '—'}</Td>
                      <Td>
                        <OutcomePill $outcome={ev.outcome}>{ev.outcome}</OutcomePill>
                      </Td>
                      <Td>{ev.ip_address || '—'}</Td>
                      <Td title={ev.user_agent}>{ev.user_agent ? ev.user_agent.substring(0, 55) + (ev.user_agent.length > 55 ? '…' : '') : '—'}</Td>
                      <Td>{ev.error_code || '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}

        {/* ── Users Tab ──────────────────────────────────────────────────── */}
        {activeTab === 'users' && (
          <>
            <ControlRow>
              <SearchInput
                type="search"
                placeholder="Search by email, name…"
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
              />
              <RefreshBtn onClick={() => fetchUsers(token)} disabled={usersLoading}>
                {usersLoading ? 'Loading…' : 'Refresh'}
              </RefreshBtn>
              <span style={{ fontSize: '0.8rem', color: '#555', marginLeft: 'auto' }}>
                {usersLoading ? '…' : `${filteredUsers.length}${userSearch ? ` of ${users.length}` : ''} user${filteredUsers.length !== 1 ? 's' : ''}`}
              </span>
            </ControlRow>

            {usersError && <ErrorMsg style={{ textAlign: 'left', marginBottom: 20 }}>{usersError}</ErrorMsg>}

            {usersLoading ? (
              <LoadingMsg>Loading users…</LoadingMsg>
            ) : filteredUsers.length === 0 ? (
              <EmptyMsg>{userSearch ? 'No users match that search.' : 'No users found.'}</EmptyMsg>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Email</Th>
                    <Th>Display Name</Th>
                    <Th>First Name</Th>
                    <Th>Last Name</Th>
                    <Th>Signed Up</Th>
                    <Th>Last Login</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(u => (
                    <tr key={u.id}>
                      <Td title={u.email}>{u.email || '—'}</Td>
                      <Td title={u.displayName}>{u.displayName || '—'}</Td>
                      <Td>{u.givenName || '—'}</Td>
                      <Td>{u.surname || '—'}</Td>
                      <Td title={u.createdDateTime}>{formatTs(u.createdDateTime)}</Td>
                      <Td title={u.lastSignIn}>{formatTs(u.lastSignIn)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}

        {/* ── Analytics Tab ──────────────────────────────────────────────── */}
        {activeTab === 'analytics' && (
          <>
            <ControlRow>
              <Select value={analyticsMonth} onChange={e => setAnalyticsMonth(e.target.value)}>
                {monthOptions.map(({ val, label }) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </Select>
              <RefreshBtn onClick={() => fetchAnalytics(token, analyticsMonth)} disabled={analyticsLoading}>
                {analyticsLoading ? 'Loading…' : 'Refresh'}
              </RefreshBtn>
            </ControlRow>

            {analyticsError && <ErrorMsg style={{ textAlign: 'left', marginBottom: 20 }}>{analyticsError}</ErrorMsg>}

            {analyticsLoading ? (
              <LoadingMsg>Loading analytics…</LoadingMsg>
            ) : analyticsData ? (
              <>
                {/* Summary cards */}
                <StatsRow>
                  <StatCard>
                    <StatNum>{analyticsData.total_events}</StatNum>
                    <StatLabel>Total View Events</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatNum>{analyticsData.unique_videos}</StatNum>
                    <StatLabel>Unique Videos</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatNum>{analyticsData.unique_users}</StatNum>
                    <StatLabel>Unique Users</StatLabel>
                  </StatCard>
                </StatsRow>

                {/* By video */}
                <SectionBlock>
                  <SectionTitle>Views by Video</SectionTitle>
                  {analyticsData.by_video.length === 0 ? (
                    <EmptyMsg style={{ padding: '20px 0' }}>No data for this period.</EmptyMsg>
                  ) : (
                    <Table>
                      <thead>
                        <tr>
                          <Th>Video ID</Th>
                          <Th>Views</Th>
                          <Th>Avg Watch (s)</Th>
                          <Th>Avg Completion %</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyticsData.by_video.map(v => (
                          <tr key={v.video_id}>
                            <Td title={v.video_id}>{v.video_id}</Td>
                            <Td>{v.views}</Td>
                            <Td>{v.avg_watch_seconds}s</Td>
                            <Td>{v.avg_completion_pct}%</Td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </SectionBlock>

                {/* By user */}
                <SectionBlock>
                  <SectionTitle>Views by User</SectionTitle>
                  {analyticsData.by_user.length === 0 ? (
                    <EmptyMsg style={{ padding: '20px 0' }}>No data for this period.</EmptyMsg>
                  ) : (
                    <Table>
                      <thead>
                        <tr>
                          <Th>Email</Th>
                          <Th>Account ID</Th>
                          <Th>Views</Th>
                          <Th>Total Watch (s)</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyticsData.by_user.map(u => (
                          <tr key={u.account_id}>
                            <Td title={u.email}>{u.email || '—'}</Td>
                            <Td title={u.account_id} style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                              {u.account_id ? u.account_id.substring(0, 20) + '…' : '—'}
                            </Td>
                            <Td>{u.views}</Td>
                            <Td>{Math.round(u.total_watch_seconds)}s</Td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </SectionBlock>

                {/* Recent events */}
                <SectionBlock>
                  <SectionTitle>Recent Events (latest 500)</SectionTitle>
                  {analyticsData.recent.length === 0 ? (
                    <EmptyMsg style={{ padding: '20px 0' }}>No events.</EmptyMsg>
                  ) : (
                    <Table>
                      <thead>
                        <tr>
                          <Th>Time</Th>
                          <Th>Video</Th>
                          <Th>Account ID</Th>
                          <Th>Watch (s)</Th>
                          <Th>%</Th>
                          <Th>Event</Th>
                          <Th>Device</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyticsData.recent.map((r, i) => (
                          <tr key={i}>
                            <Td style={{ whiteSpace: 'nowrap' }}>{formatTs(r.timestamp)}</Td>
                            <Td>{r.video_id || '—'}</Td>
                            <Td title={r.account_id} style={{ fontFamily: 'monospace', fontSize: '0.75rem', maxWidth: 160 }}>
                              {r.account_id ? r.account_id.substring(0, 24) + '…' : (r.subscriber_id ? r.subscriber_id.substring(0, 16) + '…' : '—')}
                            </Td>
                            <Td>{r.watch_time_seconds ? Math.round(r.watch_time_seconds) : 0}</Td>
                            <Td>{r.watch_percentage ? Math.round(r.watch_percentage) : 0}%</Td>
                            <Td>{r.event_type || '—'}</Td>
                            <Td>{r.device_type || '—'}</Td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </SectionBlock>
              </>
            ) : (
              <EmptyMsg>Select a month to load analytics.</EmptyMsg>
            )}
          </>
        )}
        {/* ── Agency Requests Tab (hidden — replaced by Agencies tab) */
        false && (
          <>
            <ControlRow>
              <Select value={agencyRequestsFilter} onChange={e => setAgencyRequestsFilter(e.target.value)}>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="all">All</option>
              </Select>
              <RefreshBtn onClick={() => fetchAgencyRequests(token, agencyRequestsFilter)} disabled={agencyRequestsLoading}>
                {agencyRequestsLoading ? 'Loading…' : 'Refresh'}
              </RefreshBtn>
              <span style={{ fontSize: '0.8rem', color: '#555', marginLeft: 'auto' }}>
                {agencyRequests.length} request{agencyRequests.length !== 1 ? 's' : ''}
              </span>
            </ControlRow>

            {agencyRequestsError && <ErrorMsg style={{ textAlign: 'left', marginBottom: 20 }}>{agencyRequestsError}</ErrorMsg>}

            {agencyRequestsLoading ? (
              <LoadingMsg>Loading requests…</LoadingMsg>
            ) : agencyRequests.length === 0 ? (
              <EmptyMsg>No agency requests in this filter.</EmptyMsg>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th>Agency</Th>
                    <Th>Website</Th>
                    <Th>Applied</Th>
                    <Th>Status</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {agencyRequests.map(r => (
                    <tr key={r.oid}>
                      <Td title={r.display_name}>{r.display_name || '—'}</Td>
                      <Td title={r.email}>{r.email || '—'}</Td>
                      <Td title={r.agency_name}>{r.agency_name || '—'}</Td>
                      <Td title={r.website}>
                        {r.website ? (
                          <a href={r.website} target="_blank" rel="noopener noreferrer" style={{ color: '#aaa' }}>
                            {r.website.replace(/^https?:\/\//, '').substring(0, 28)}
                          </a>
                        ) : '—'}
                      </Td>
                      <Td title={r.submitted_at}>{formatTs(r.submitted_at)}</Td>
                      <Td>
                        <OutcomePill $outcome={r.status === 'approved' ? 'success' : r.status === 'rejected' ? 'failure' : undefined}
                          style={r.status === 'pending' ? { background: '#1a1a10', color: '#ffc800', border: '1px solid #5c5010' } : {}}>
                          {r.status}
                        </OutcomePill>
                      </Td>
                      <Td style={{ whiteSpace: 'nowrap' }}>
                        {r.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleAgencyApprove(r.oid)}
                              disabled={agencyActionLoading === r.oid}
                              style={{
                                background: '#0d2b1a', border: '1px solid #1a5c34', color: '#2ecc71',
                                borderRadius: 5, padding: '4px 10px', fontSize: '0.76rem', cursor: 'pointer',
                                marginRight: 6, fontWeight: 600,
                              }}
                            >
                              {agencyActionLoading === r.oid ? '…' : 'Approve'}
                            </button>
                            <button
                              onClick={() => setRejectModal({ open: true, oid: r.oid, name: r.display_name || r.email, reason: '' })}
                              disabled={agencyActionLoading === r.oid}
                              style={{
                                background: '#2b0d0d', border: '1px solid #5c1a1a', color: '#ff4757',
                                borderRadius: 5, padding: '4px 10px', fontSize: '0.76rem', cursor: 'pointer',
                                fontWeight: 600,
                              }}
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {r.status === 'rejected' && r.rejection_reason && (
                          <span style={{ fontSize: '0.75rem', color: '#888' }} title={r.rejection_reason}>
                            Reason: {r.rejection_reason.substring(0, 40)}{r.rejection_reason.length > 40 ? '…' : ''}
                          </span>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}

        {/* ── Agencies Tab ────────────────────────────────────────────── */}
        {activeTab === 'agencies' && (
          <>
            {/* Toast notifications */}
            <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {agencyToasts.map(t => (
                <div key={t.id} style={{
                  background: t.type === 'err' ? '#2b0d0d' : '#0d2b1a',
                  border: `1px solid ${t.type === 'err' ? '#5c1a1a' : '#1a5c34'}`,
                  color: t.type === 'err' ? '#ff4757' : '#2ecc71',
                  padding: '10px 16px', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600,
                }}>{t.msg}</div>
              ))}
            </div>

            <ControlRow>
              <input
                type="text"
                placeholder="New agency name"
                value={newAgencyName}
                onChange={e => setNewAgencyName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreateAgency()}
                style={{
                  background: '#111', border: '1px solid #333', color: 'white', borderRadius: 7,
                  padding: '8px 12px', fontSize: '0.85rem', flex: 1, maxWidth: 280,
                }}
              />
              <button
                onClick={handleCreateAgency}
                disabled={newAgencyCreating || !newAgencyName.trim()}
                style={{
                  background: '#0d2b1a', border: '1px solid #1a5c34', color: '#2ecc71',
                  borderRadius: 7, padding: '8px 16px', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600,
                }}
              >
                {newAgencyCreating ? 'Creating…' : '+ New Agency'}
              </button>
              <RefreshBtn onClick={() => fetchAgencies(token)} disabled={agenciesLoading}>
                {agenciesLoading ? 'Loading…' : 'Refresh'}
              </RefreshBtn>
            </ControlRow>

            {agenciesError && <ErrorMsg style={{ textAlign: 'left', marginBottom: 16 }}>{agenciesError}</ErrorMsg>}

            {agenciesLoading && agencies.length === 0 ? (
              <LoadingMsg>Loading agencies…</LoadingMsg>
            ) : agencies.length === 0 ? (
              <EmptyMsg>No agencies yet. Create one above.</EmptyMsg>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {agencies.map(ag => (
                  <div key={ag.agency_id} style={{
                    background: '#111', border: '1px solid #2a2a2a', borderRadius: 10, overflow: 'hidden',
                  }}>
                    {/* Agency header row */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                      cursor: 'pointer',
                    }} onClick={() => {
                      if (agencyExpandedId === ag.agency_id) {
                        setAgencyExpandedId(null);
                      } else {
                        setAgencyExpandedId(ag.agency_id);
                        if (!agencyUsers[ag.agency_id]) fetchAgencyUsers(token, ag.agency_id);
                      }
                    }}>
                      <span style={{ fontSize: '0.9rem', fontWeight: 700, flex: 1 }}>{ag.name}</span>
                      <OutcomePill $outcome={ag.status === 'active' ? 'success' : 'failure'}>{ag.status}</OutcomePill>
                      <span style={{ fontSize: '0.78rem', color: '#555' }}>{ag.user_count} user{ag.user_count !== 1 ? 's' : ''}</span>
                      <button
                        onClick={e => { e.stopPropagation(); handleToggleAgencyStatus(ag); }}
                        style={{
                          background: ag.status === 'active' ? '#2b0d0d' : '#0d2b1a',
                          border: `1px solid ${ag.status === 'active' ? '#5c1a1a' : '#1a5c34'}`,
                          color: ag.status === 'active' ? '#ff4757' : '#2ecc71',
                          borderRadius: 5, padding: '3px 10px', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 600,
                        }}
                      >
                        {ag.status === 'active' ? 'Suspend' : 'Unsuspend'}
                      </button>
                      <span style={{ color: '#444', fontSize: '0.85rem' }}>{agencyExpandedId === ag.agency_id ? '▲' : '▼'}</span>
                    </div>

                    {/* Expanded user list */}
                    {agencyExpandedId === ag.agency_id && (
                      <div style={{ borderTop: '1px solid #222', padding: '14px 16px', background: '#0c0c0c' }}>
                        {agencyUsersLoading === ag.agency_id ? (
                          <LoadingMsg>Loading users…</LoadingMsg>
                        ) : (
                          <>
                            {(agencyUsers[ag.agency_id] || []).length === 0 ? (
                              <p style={{ color: '#555', fontSize: '0.82rem', margin: '0 0 12px' }}>No users yet.</p>
                            ) : (
                              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 14, fontSize: '0.82rem' }}>
                                <thead>
                                  <tr style={{ color: '#555', textAlign: 'left' }}>
                                    <th style={{ padding: '4px 8px' }}>Email</th>
                                    <th style={{ padding: '4px 8px' }}>Role</th>
                                    <th style={{ padding: '4px 8px' }}>Status</th>
                                    <th style={{ padding: '4px 8px' }}>Last login</th>
                                    <th style={{ padding: '4px 8px' }}>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(agencyUsers[ag.agency_id] || []).map(u => (
                                    <tr key={u.email} style={{ borderTop: '1px solid #1a1a1a' }}>
                                      <Td style={{ fontFamily: 'monospace' }}>{u.email}</Td>
                                      <Td>
                                        <select
                                          value={u.role}
                                          disabled={editRoleLoading === u.email}
                                          onChange={e => handleEditUserRole(ag.agency_id, u.email, e.target.value)}
                                          style={{
                                            background: '#111', border: '1px solid #333', color: 'white',
                                            borderRadius: 4, padding: '2px 6px', fontSize: '0.78rem', cursor: 'pointer',
                                            opacity: editRoleLoading === u.email ? 0.5 : 1,
                                          }}
                                        >
                                          <option value="member">member</option>
                                          <option value="owner">owner</option>
                                        </select>
                                      </Td>
                                      <Td>
                                        <OutcomePill $outcome={u.status === 'active' ? 'success' : undefined}
                                          style={u.status === 'invited' ? { background: '#1a1a10', color: '#ffc800', border: '1px solid #5c5010' } : {}}>
                                          {u.status}
                                        </OutcomePill>
                                      </Td>
                                      <Td style={{ color: '#555' }}>{u.last_login_at ? formatTs(u.last_login_at) : '—'}</Td>
                                      <Td style={{ whiteSpace: 'nowrap' }}>
                                        <button
                                          onClick={() => handleGenerateInvite(ag.agency_id, u.email)}
                                          disabled={inviteLoading === u.email}
                                          style={{
                                            background: '#10101c', border: '1px solid #333', color: inviteCopied === u.email ? '#2ecc71' : '#aaa',
                                            borderRadius: 5, padding: '3px 9px', fontSize: '0.75rem', cursor: 'pointer',
                                            marginRight: 6, fontWeight: 600,
                                          }}
                                        >
                                          {inviteLoading === u.email ? '…' : inviteCopied === u.email ? '✓ Copied!' : '🔗 Invite link'}
                                        </button>
                                        <button
                                          onClick={() => handleRemoveUser(ag.agency_id, u.email)}
                                          style={{
                                            background: '#2b0d0d', border: '1px solid #5c1a1a', color: '#ff4757',
                                            borderRadius: 5, padding: '3px 9px', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 600,
                                          }}
                                        >
                                          Remove
                                        </button>
                                      </Td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}

                            {/* Add user row */}
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <input
                                type="email"
                                placeholder="user@agency.com"
                                value={addUserEmail}
                                onChange={e => setAddUserEmail(e.target.value)}
                                style={{
                                  background: '#111', border: '1px solid #333', color: 'white',
                                  borderRadius: 6, padding: '6px 10px', fontSize: '0.82rem', flex: 1,
                                }}
                              />
                              <select
                                value={addUserRole}
                                onChange={e => setAddUserRole(e.target.value)}
                                style={{
                                  background: '#111', border: '1px solid #333', color: 'white',
                                  borderRadius: 6, padding: '6px 8px', fontSize: '0.82rem',
                                }}
                              >
                                <option value="member">Member</option>
                                <option value="owner">Owner</option>
                              </select>
                              <button
                                onClick={() => handleAddUser(ag.agency_id)}
                                disabled={addUserLoading === ag.agency_id || !addUserEmail}
                                style={{
                                  background: '#0d2b1a', border: '1px solid #1a5c34', color: '#2ecc71',
                                  borderRadius: 6, padding: '6px 14px', fontSize: '0.82rem', cursor: 'pointer', fontWeight: 600,
                                }}
                              >
                                {addUserLoading === ag.agency_id ? 'Adding…' : 'Add User'}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Publishers Tab ─────────────────────────────────────────── */}
        {activeTab === 'publishers' && (
          <>
            <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {publisherToasts.map(t => (
                <div key={t.id} style={{
                  background: t.type === 'err' ? '#2b0d0d' : '#0d2b1a',
                  border: `1px solid ${t.type === 'err' ? '#5c1a1a' : '#1a5c34'}`,
                  color: t.type === 'err' ? '#ff4757' : '#2ecc71',
                  padding: '10px 16px', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600,
                }}>{t.msg}</div>
              ))}
            </div>

            <ControlRow>
              <input
                type="text"
                placeholder="New publisher name"
                value={newPublisherName}
                onChange={e => setNewPublisherName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreatePublisher()}
                style={{
                  background: '#111', border: '1px solid #333', color: 'white', borderRadius: 7,
                  padding: '8px 12px', fontSize: '0.85rem', flex: 1, maxWidth: 280,
                }}
              />
              <button
                onClick={handleCreatePublisher}
                disabled={newPublisherCreating || !newPublisherName.trim()}
                style={{
                  background: '#0d2b1a', border: '1px solid #1a5c34', color: '#2ecc71',
                  borderRadius: 7, padding: '8px 16px', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600,
                }}
              >
                {newPublisherCreating ? 'Creating…' : '+ New Publisher'}
              </button>
              <RefreshBtn onClick={() => fetchPublishers(token)} disabled={publishersLoading}>
                {publishersLoading ? 'Loading…' : 'Refresh'}
              </RefreshBtn>
            </ControlRow>

            {publishersError && <ErrorMsg style={{ textAlign: 'left', marginBottom: 16 }}>{publishersError}</ErrorMsg>}

            {publishersLoading && publishers.length === 0 ? (
              <LoadingMsg>Loading publishers…</LoadingMsg>
            ) : publishers.length === 0 ? (
              <EmptyMsg>No publishers yet. Create one above.</EmptyMsg>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {publishers.map(pub => (
                  <div key={pub.publisher_id} style={{
                    background: '#111', border: '1px solid #2a2a2a', borderRadius: 10, overflow: 'hidden',
                  }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer',
                    }} onClick={() => {
                      if (publisherExpandedId === pub.publisher_id) {
                        setPublisherExpandedId(null);
                      } else {
                        setPublisherExpandedId(pub.publisher_id);
                        if (!publisherUsers[pub.publisher_id]) fetchPublisherUsers(token, pub.publisher_id);
                      }
                    }}>
                      <span style={{ fontSize: '0.9rem', fontWeight: 700, flex: 1 }}>{pub.name}</span>
                      <OutcomePill $outcome={pub.status === 'active' ? 'success' : 'failure'}>{pub.status}</OutcomePill>
                      <span style={{ fontSize: '0.78rem', color: '#555' }}>{pub.user_count} user{pub.user_count !== 1 ? 's' : ''}</span>
                      <button
                        onClick={e => { e.stopPropagation(); handleTogglePublisherStatus(pub); }}
                        style={{
                          background: pub.status === 'active' ? '#2b0d0d' : '#0d2b1a',
                          border: `1px solid ${pub.status === 'active' ? '#5c1a1a' : '#1a5c34'}`,
                          color: pub.status === 'active' ? '#ff4757' : '#2ecc71',
                          borderRadius: 5, padding: '3px 10px', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 600,
                        }}
                      >
                        {pub.status === 'active' ? 'Suspend' : 'Unsuspend'}
                      </button>
                      <span style={{ color: '#444', fontSize: '0.85rem' }}>{publisherExpandedId === pub.publisher_id ? '▲' : '▼'}</span>
                    </div>

                    {publisherExpandedId === pub.publisher_id && (
                      <div style={{ borderTop: '1px solid #222', padding: '14px 16px', background: '#0c0c0c' }}>
                        {publisherUsersLoading === pub.publisher_id ? (
                          <LoadingMsg>Loading users…</LoadingMsg>
                        ) : (
                          <>
                            {(publisherUsers[pub.publisher_id] || []).length === 0 ? (
                              <p style={{ color: '#555', fontSize: '0.82rem', margin: '0 0 12px' }}>No users yet.</p>
                            ) : (
                              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 14, fontSize: '0.82rem' }}>
                                <thead>
                                  <tr style={{ color: '#555', textAlign: 'left' }}>
                                    <th style={{ padding: '4px 8px' }}>Email</th>
                                    <th style={{ padding: '4px 8px' }}>Role</th>
                                    <th style={{ padding: '4px 8px' }}>Status</th>
                                    <th style={{ padding: '4px 8px' }}>Last login</th>
                                    <th style={{ padding: '4px 8px' }}>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(publisherUsers[pub.publisher_id] || []).map(u => (
                                    <tr key={u.email} style={{ borderTop: '1px solid #1a1a1a' }}>
                                      <Td style={{ fontFamily: 'monospace' }}>{u.email}</Td>
                                      <Td>
                                        <select
                                          value={u.role}
                                          disabled={pubEditRoleLoading === u.email}
                                          onChange={e => handleEditPublisherUserRole(pub.publisher_id, u.email, e.target.value)}
                                          style={{
                                            background: '#111', border: '1px solid #333', color: 'white',
                                            borderRadius: 4, padding: '2px 6px', fontSize: '0.78rem', cursor: 'pointer',
                                            opacity: pubEditRoleLoading === u.email ? 0.5 : 1,
                                          }}
                                        >
                                          <option value="member">member</option>
                                          <option value="owner">owner</option>
                                        </select>
                                      </Td>
                                      <Td>
                                        <OutcomePill $outcome={u.status === 'active' ? 'success' : undefined}
                                          style={u.status === 'invited' ? { background: '#1a1a10', color: '#ffc800', border: '1px solid #5c5010' } : {}}>
                                          {u.status}
                                        </OutcomePill>
                                      </Td>
                                      <Td style={{ color: '#555' }}>{u.last_login_at ? formatTs(u.last_login_at) : '—'}</Td>
                                      <Td style={{ whiteSpace: 'nowrap' }}>
                                        <button
                                          onClick={() => handleGeneratePublisherInvite(pub.publisher_id, u.email)}
                                          disabled={pubInviteLoading === u.email}
                                          style={{
                                            background: '#10101c', border: '1px solid #333', color: pubInviteCopied === u.email ? '#2ecc71' : '#aaa',
                                            borderRadius: 5, padding: '3px 9px', fontSize: '0.75rem', cursor: 'pointer',
                                            marginRight: 6, fontWeight: 600,
                                          }}
                                        >
                                          {pubInviteLoading === u.email ? '…' : pubInviteCopied === u.email ? '✓ Copied!' : '🔗 Invite link'}
                                        </button>
                                        <button
                                          onClick={() => handleRemovePublisherUser(pub.publisher_id, u.email)}
                                          style={{
                                            background: '#2b0d0d', border: '1px solid #5c1a1a', color: '#ff4757',
                                            borderRadius: 5, padding: '3px 9px', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 600,
                                          }}
                                        >
                                          Remove
                                        </button>
                                      </Td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}

                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <input
                                type="email"
                                placeholder="owner@publisher.com"
                                value={pubAddUserEmail}
                                onChange={e => setPubAddUserEmail(e.target.value)}
                                style={{
                                  background: '#111', border: '1px solid #333', color: 'white',
                                  borderRadius: 6, padding: '6px 10px', fontSize: '0.82rem', flex: 1,
                                }}
                              />
                              <select
                                value={pubAddUserRole}
                                onChange={e => setPubAddUserRole(e.target.value)}
                                style={{
                                  background: '#111', border: '1px solid #333', color: 'white',
                                  borderRadius: 6, padding: '6px 8px', fontSize: '0.82rem',
                                }}
                              >
                                <option value="owner">Owner</option>
                                <option value="member">Member</option>
                              </select>
                              <button
                                onClick={() => handleAddPublisherUser(pub.publisher_id)}
                                disabled={pubAddUserLoading === pub.publisher_id || !pubAddUserEmail}
                                style={{
                                  background: '#0d2b1a', border: '1px solid #1a5c34', color: '#2ecc71',
                                  borderRadius: 6, padding: '6px 14px', fontSize: '0.82rem', cursor: 'pointer', fontWeight: 600,
                                }}
                              >
                                {pubAddUserLoading === pub.publisher_id ? 'Adding…' : 'Add User'}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Catalog Tab ────────────────────────────────────────────── */}
        {activeTab === 'catalog' && (
          <>
            <ControlRow>
              <SearchInput
                type="search"
                placeholder="Search filename or title…"
                value={catalogSearch}
                onChange={e => setCatalogSearch(e.target.value)}
              />
              <RefreshBtn onClick={() => fetchCatalog(token)} disabled={catalogLoading}>
                {catalogLoading ? 'Loading…' : 'Refresh'}
              </RefreshBtn>
              <button
                onClick={() => setCatalogAddModal(true)}
                style={{
                  background: '#0d2b1a', border: '1px solid #1a5c34', color: '#2ecc71',
                  borderRadius: 7, padding: '8px 16px', fontSize: '0.85rem',
                  cursor: 'pointer', fontWeight: 600,
                }}
              >
                + Add Video
              </button>
              <span style={{ fontSize: '0.8rem', color: '#555', marginLeft: 'auto' }}>
                {filteredCatalog.length}{catalogSearch ? ` of ${catalogVideos.length}` : ''} videos
              </span>
            </ControlRow>

            {catalogError && <ErrorMsg style={{ textAlign: 'left', marginBottom: 20 }}>{catalogError}</ErrorMsg>}

            {catalogLoading ? (
              <LoadingMsg>Loading catalog…</LoadingMsg>
            ) : filteredCatalog.length === 0 ? (
              <EmptyMsg>{catalogSearch ? 'No videos match that search.' : 'Catalog is empty — add videos using the button above.'}</EmptyMsg>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Filename</Th>
                    <Th>Title</Th>
                    <Th>Pts</Th>
                    <Th>Advertiser URL</Th>
                    <Th>Active</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCatalog.map(v => (
                    <tr key={v.filename} style={{ opacity: v.active ? 1 : 0.45 }}>
                      <Td title={v.filename} style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{v.filename}</Td>
                      <Td title={v.title}>{v.title || '—'}</Td>
                      <Td style={{ color: '#ff4757', fontWeight: 600 }}>{v.points}</Td>
                      <Td title={v.url}>
                        {v.url ? (
                          <a href={v.url} target="_blank" rel="noopener noreferrer" style={{ color: '#aaa' }}>
                            {v.url.replace(/^https?:\/\//, '').substring(0, 30)}{v.url.length > 33 ? '…' : ''}
                          </a>
                        ) : '—'}
                      </Td>
                      <Td>
                        <OutcomePill $outcome={v.active ? 'success' : 'failure'}>
                          {v.active ? 'Active' : 'Hidden'}
                        </OutcomePill>
                      </Td>
                      <Td style={{ whiteSpace: 'nowrap' }}>
                        <button
                          onClick={() => setCatalogEditModal({ ...v })}
                          style={{
                            background: '#1a1a2e', border: '1px solid #333', color: '#ccc',
                            borderRadius: 5, padding: '3px 10px', fontSize: '0.76rem',
                            cursor: 'pointer', marginRight: 6,
                          }}
                        >Edit</button>
                        <button
                          onClick={() => handleCatalogToggleActive(v.filename, v.active)}
                          style={{
                            background: v.active ? '#2b0d0d' : '#0d2b1a',
                            border: `1px solid ${v.active ? '#5c1a1a' : '#1a5c34'}`,
                            color: v.active ? '#ff4757' : '#2ecc71',
                            borderRadius: 5, padding: '3px 10px', fontSize: '0.76rem', cursor: 'pointer',
                          }}
                        >{v.active ? 'Hide' : 'Show'}</button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}

        {/* Catalog — Edit Modal */}
        {catalogEditModal && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              background: '#111118', border: '1px solid #2a2a36', borderRadius: 12,
              padding: '28px 28px 24px', width: 440, maxWidth: '92vw',
            }}>
              <h3 style={{ color: 'white', margin: '0 0 4px', fontSize: '1rem' }}>Edit video</h3>
              <p style={{ color: '#555', fontFamily: 'monospace', fontSize: '0.78rem', margin: '0 0 18px' }}>{catalogEditModal.filename}</p>
              <Field>
                <Label>Title</Label>
                <Input value={catalogEditModal.title} onChange={e => setCatalogEditModal(m => ({ ...m, title: e.target.value }))} />
              </Field>
              <Field>
                <Label>Description</Label>
                <Input value={catalogEditModal.description} onChange={e => setCatalogEditModal(m => ({ ...m, description: e.target.value }))} />
              </Field>
              <Field>
                <Label>Points</Label>
                <Input type="number" min={1} max={999} value={catalogEditModal.points} onChange={e => setCatalogEditModal(m => ({ ...m, points: e.target.value }))} />
              </Field>
              <Field>
                <Label>Advertiser URL</Label>
                <Input value={catalogEditModal.url} onChange={e => setCatalogEditModal(m => ({ ...m, url: e.target.value }))} />
              </Field>
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <LoginBtn onClick={handleCatalogSave} disabled={catalogSaving}>
                  {catalogSaving ? 'Saving…' : 'Save'}
                </LoginBtn>
                <LoginBtn
                  style={{ background: '#1a1a24', border: '1px solid #333', color: '#888' }}
                  onClick={() => setCatalogEditModal(null)}
                >
                  Cancel
                </LoginBtn>
              </div>
              {catalogError && <ErrorMsg style={{ marginTop: 10 }}>{catalogError}</ErrorMsg>}
            </div>
          </div>
        )}

        {/* Catalog — Add Modal */}
        {catalogAddModal && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              background: '#111118', border: '1px solid #2a2a36', borderRadius: 12,
              padding: '28px 28px 24px', width: 440, maxWidth: '92vw',
            }}>
              <h3 style={{ color: 'white', margin: '0 0 18px', fontSize: '1rem' }}>Add video to catalog</h3>
              <Field>
                <Label>Filename <span style={{ color: '#ff4757' }}>*</span></Label>
                <Input
                  placeholder="e.g. MyBrand.mp4"
                  value={catalogAddForm.filename}
                  onChange={e => setCatalogAddForm(f => ({ ...f, filename: e.target.value }))}
                />
              </Field>
              <Field>
                <Label>Title <span style={{ color: '#ff4757' }}>*</span></Label>
                <Input
                  value={catalogAddForm.title}
                  onChange={e => setCatalogAddForm(f => ({ ...f, title: e.target.value }))}
                />
              </Field>
              <Field>
                <Label>Description</Label>
                <Input
                  value={catalogAddForm.description}
                  onChange={e => setCatalogAddForm(f => ({ ...f, description: e.target.value }))}
                />
              </Field>
              <Field>
                <Label>Points</Label>
                <Input
                  type="number" min={1} max={999}
                  value={catalogAddForm.points}
                  onChange={e => setCatalogAddForm(f => ({ ...f, points: e.target.value }))}
                />
              </Field>
              <Field>
                <Label>Advertiser URL</Label>
                <Input
                  placeholder="https://"
                  value={catalogAddForm.url}
                  onChange={e => setCatalogAddForm(f => ({ ...f, url: e.target.value }))}
                />
              </Field>
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <LoginBtn
                  onClick={handleCatalogAdd}
                  disabled={catalogSaving || !catalogAddForm.filename.trim() || !catalogAddForm.title.trim()}
                >
                  {catalogSaving ? 'Adding…' : 'Add Video'}
                </LoginBtn>
                <LoginBtn
                  style={{ background: '#1a1a24', border: '1px solid #333', color: '#888' }}
                  onClick={() => { setCatalogAddModal(false); setCatalogAddForm({ filename: '', title: '', description: '', points: 5, url: '' }); }}
                >
                  Cancel
                </LoginBtn>
              </div>
              {catalogError && <ErrorMsg style={{ marginTop: 10 }}>{catalogError}</ErrorMsg>}
            </div>
          </div>
        )}

        {/* ── Ad Review Tab ───────────────────────────────────────────── */}
        {activeTab === 'adreview' && (
          <>
            <ControlRow>
              <span style={{ color: '#888', fontSize: '0.85rem' }}>
                Videos submitted by agencies awaiting approval
              </span>
              <RefreshBtn onClick={() => {
                setPendingAdsLoading(true);
                setPendingAdsError('');
                fetch(`${API_URL}/api/admin/agency-videos/pending`, { headers: { 'X-Admin-Token': token } })
                  .then(r => r.ok ? r.json() : r.json().then(d => { throw new Error(d.detail || `Error ${r.status}`); }))
                  .then(d => setPendingAds(d.videos || []))
                  .catch(e => setPendingAdsError(e.message))
                  .finally(() => setPendingAdsLoading(false));
              }} disabled={pendingAdsLoading}>
                {pendingAdsLoading ? 'Loading…' : 'Refresh'}
              </RefreshBtn>
            </ControlRow>
            {pendingAdsError && <ErrorMsg>{pendingAdsError}</ErrorMsg>}
            {adActionToast && (
              <div style={{ background: '#0d2b1a', border: '1px solid #1a5c34', color: '#2ecc71', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: '0.85rem' }}>
                {adActionToast}
              </div>
            )}
            {!pendingAdsLoading && pendingAds.length === 0 && !pendingAdsError && (
              <div style={{ color: '#555', fontSize: '0.9rem', padding: '32px 0', textAlign: 'center' }}>No videos pending review.</div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 18 }}>
              {pendingAds.map(v => (
                <div key={v.video_id} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14, overflow: 'hidden' }}>
                  {v.blob_url
                    ? <video src={v.blob_url} controls preload="metadata" style={{ width: '100%', aspectRatio: '16/9', background: '#0a0a14', display: 'block', objectFit: 'cover' }} />
                    : <div style={{ width: '100%', aspectRatio: '16/9', background: '#0a0a14', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333', fontSize: '2rem' }}>▶</div>
                  }
                  <div style={{ padding: '14px 16px' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 4 }}>{v.title}</div>
                    <div style={{ fontSize: '0.75rem', color: '#555', marginBottom: 12, fontFamily: 'monospace' }}>
                      Agency: {v.agency_oid} &nbsp;·&nbsp; Submitted: {v.updated_at ? new Date(v.updated_at).toLocaleDateString() : '—'}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        disabled={adActionLoading === v.video_id}
                        onClick={async () => {
                          setAdActionLoading(v.video_id);
                          try {
                            const r = await fetch(`${API_URL}/api/admin/agency-videos/${v.video_id}/approve?agency_oid=${encodeURIComponent(v.agency_oid)}`, {
                              method: 'PATCH', headers: { 'X-Admin-Token': token },
                            });
                            if (!r.ok) { const d = await r.json(); throw new Error(d.detail || `Error ${r.status}`); }
                            setPendingAds(prev => prev.filter(x => x.video_id !== v.video_id));
                            setAdActionToast(`✓ Approved "${v.title}"`);
                            setTimeout(() => setAdActionToast(''), 3000);
                          } catch (e) { setPendingAdsError(e.message); }
                          finally { setAdActionLoading(null); }
                        }}
                        style={{ flex: 1, background: '#0d2b1a', border: '1px solid #1a5c34', color: '#2ecc71', borderRadius: 7, padding: '8px 0', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', opacity: adActionLoading === v.video_id ? 0.5 : 1 }}
                      >
                        {adActionLoading === v.video_id ? '…' : '✓ Approve'}
                      </button>
                      <button
                        disabled={adActionLoading === v.video_id}
                        onClick={() => setAdRejectModal({ open: true, video_id: v.video_id, agency_oid: v.agency_oid, title: v.title, reason: '' })}
                        style={{ flex: 1, background: '#2b0d0d', border: '1px solid #5c1a1a', color: '#ff4757', borderRadius: 7, padding: '8px 0', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', opacity: adActionLoading === v.video_id ? 0.5 : 1 }}
                      >
                        ✕ Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Ad reject modal */}
        {adRejectModal.open && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#111118', border: '1px solid #2a2a36', borderRadius: 12, padding: '28px 28px 24px', width: 380, maxWidth: '90vw' }}>
              <h3 style={{ color: 'white', margin: '0 0 6px', fontSize: '1rem' }}>Reject ad</h3>
              <p style={{ color: '#666', fontSize: '0.82rem', margin: '0 0 16px' }}>{adRejectModal.title}</p>
              <Label>Reason (shown to agency)</Label>
              <Input
                placeholder="e.g. Video quality too low"
                value={adRejectModal.reason}
                onChange={e => setAdRejectModal(m => ({ ...m, reason: e.target.value }))}
                style={{ marginBottom: 16 }}
              />
              <div style={{ display: 'flex', gap: 10 }}>
                <LoginBtn
                  style={{ background: '#2b0d0d', border: '1px solid #5c1a1a', color: '#ff4757' }}
                  disabled={adActionLoading === adRejectModal.video_id}
                  onClick={async () => {
                    setAdActionLoading(adRejectModal.video_id);
                    try {
                      const r = await fetch(`${API_URL}/api/admin/agency-videos/${adRejectModal.video_id}/reject?agency_oid=${encodeURIComponent(adRejectModal.agency_oid)}`, {
                        method: 'PATCH',
                        headers: { 'X-Admin-Token': token, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ rejection_reason: adRejectModal.reason }),
                      });
                      if (!r.ok) { const d = await r.json(); throw new Error(d.detail || `Error ${r.status}`); }
                      setPendingAds(prev => prev.filter(x => x.video_id !== adRejectModal.video_id));
                      setAdRejectModal({ open: false, video_id: '', agency_oid: '', title: '', reason: '' });
                      setAdActionToast(`Rejected "${adRejectModal.title}"`);
                      setTimeout(() => setAdActionToast(''), 3000);
                    } catch (e) { setPendingAdsError(e.message); }
                    finally { setAdActionLoading(null); }
                  }}
                >
                  {adActionLoading === adRejectModal.video_id ? 'Rejecting…' : 'Confirm reject'}
                </LoginBtn>
                <LoginBtn
                  style={{ background: '#1a1a24', border: '1px solid #333', color: '#888' }}
                  onClick={() => setAdRejectModal({ open: false, video_id: '', agency_oid: '', title: '', reason: '' })}
                >
                  Cancel
                </LoginBtn>
              </div>
            </div>
          </div>
        )}

        {/* Reject modal */}
        {rejectModal.open && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              background: '#111118', border: '1px solid #2a2a36', borderRadius: 12,
              padding: '28px 28px 24px', width: 380, maxWidth: '90vw',
            }}>
              <h3 style={{ color: 'white', margin: '0 0 6px', fontSize: '1rem' }}>Reject application</h3>
              <p style={{ color: '#666', fontSize: '0.82rem', margin: '0 0 16px' }}>{rejectModal.name}</p>
              <Label htmlFor="reject-reason">Reason (shown to applicant)</Label>
              <Input
                id="reject-reason"
                placeholder="Optional reason…"
                value={rejectModal.reason}
                onChange={e => setRejectModal(m => ({ ...m, reason: e.target.value }))}
                style={{ marginBottom: 16 }}
              />
              <div style={{ display: 'flex', gap: 10 }}>
                <LoginBtn
                  style={{ background: '#2b0d0d', border: '1px solid #5c1a1a', color: '#ff4757' }}
                  onClick={async () => {
                    await handleAgencyReject(rejectModal.oid, rejectModal.reason);
                    setRejectModal({ open: false, oid: '', name: '', reason: '' });
                  }}
                  disabled={agencyActionLoading === rejectModal.oid}
                >
                  {agencyActionLoading === rejectModal.oid ? 'Rejecting…' : 'Confirm reject'}
                </LoginBtn>
                <LoginBtn
                  style={{ background: '#1a1a24', border: '1px solid #333', color: '#888' }}
                  onClick={() => setRejectModal({ open: false, oid: '', name: '', reason: '' })}
                >
                  Cancel
                </LoginBtn>
              </div>
            </div>
          </div>
        )}

        {/* ── Migrations Tab ─────────────────────────────────────────── */}
        {activeTab === 'migrations' && (
          <>
            <ControlRow>
              <span style={{ color: '#888', fontSize: '0.85rem' }}>
                One-off data migrations. Each runs at most once; running an already-completed one is a safe no-op.
              </span>
              <RefreshBtn onClick={() => fetchMigrations(token)} disabled={migrationsLoading}>
                {migrationsLoading ? 'Loading…' : 'Refresh'}
              </RefreshBtn>
            </ControlRow>

            {migrationsError && <ErrorMsg style={{ textAlign: 'left', marginBottom: 16 }}>{migrationsError}</ErrorMsg>}
            {migrationResult && (
              <div style={{
                background: '#0d2b1a', border: '1px solid #1a5c34', color: '#2ecc71',
                borderRadius: 8, padding: '10px 16px', marginBottom: 14,
                fontSize: '0.82rem', fontFamily: 'monospace', wordBreak: 'break-all',
              }}>
                {migrationResult}
              </div>
            )}

            {migrationsLoading && migrationsList.length === 0 ? (
              <LoadingMsg>Loading migrations…</LoadingMsg>
            ) : migrationsList.length === 0 ? (
              <EmptyMsg>No migrations registered.</EmptyMsg>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>ID</Th>
                    <Th>Description</Th>
                    <Th>Status</Th>
                    <Th>Last run</Th>
                    <Th>By</Th>
                    <Th>Result</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {migrationsList.map(m => {
                    const done = m.status === 'completed';
                    const failed = m.status === 'failed';
                    const notesStr = m.notes && typeof m.notes === 'object' && Object.keys(m.notes).length
                      ? JSON.stringify(m.notes) : '';
                    return (
                      <tr key={m.id}>
                        <Td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }} title={m.id}>{m.id}</Td>
                        <Td title={m.description} style={{ whiteSpace: 'normal' }}>{m.description}</Td>
                        <Td>
                          <OutcomePill
                            $outcome={done ? 'success' : failed ? 'failure' : undefined}
                            style={!done && !failed ? { background: '#1a1a10', color: '#ffc800', border: '1px solid #5c5010' } : {}}
                          >
                            {m.status}
                          </OutcomePill>
                        </Td>
                        <Td style={{ color: '#666' }}>{m.completed_at ? formatTs(m.completed_at) : '—'}</Td>
                        <Td style={{ color: '#666' }}>{m.ran_by || '—'}</Td>
                        <Td title={failed ? m.error : notesStr} style={{ color: failed ? '#ff4757' : '#888', fontSize: '0.76rem' }}>
                          {failed ? (m.error || 'failed') : (notesStr || '—')}
                        </Td>
                        <Td style={{ whiteSpace: 'nowrap' }}>
                          <button
                            onClick={() => handleRunMigration(m)}
                            disabled={migrationRunning === m.id || m.orphaned}
                            title={m.orphaned ? 'Not in the code registry — cannot run' : ''}
                            style={{
                              background: done ? '#1a1a2e' : '#0d2b1a',
                              border: `1px solid ${done ? '#333' : '#1a5c34'}`,
                              color: done ? '#ccc' : '#2ecc71',
                              borderRadius: 5, padding: '3px 12px', fontSize: '0.76rem',
                              cursor: m.orphaned ? 'default' : 'pointer', fontWeight: 600,
                              opacity: (migrationRunning === m.id || m.orphaned) ? 0.5 : 1,
                            }}
                          >
                            {migrationRunning === m.id ? 'Running…' : done ? 'Re-run' : 'Run'}
                          </button>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </>
        )}
      </DashContent>
    </Portal>
  );
}
